import type {
  Appointment,
  Branch,
  Chair,
  NoShowRisk,
  WaitlistEntry,
} from "@dentacore/shared-types";

import { todayIn } from "../calendar/zoned-time";

/** The statuses a booking is finished in, so it stops counting as remaining. */
const SETTLED = new Set(["COMPLETED", "CANCELLED", "NO_SHOW"]);

export interface HomeData {
  appointments: readonly Appointment[];
  waitlist: readonly WaitlistEntry[];
  chairs: readonly Chair[];
  branch: Branch | null;
}

/**
 * Today, as `YYYY-MM-DD`, **in the location's zone** (P2 Q8).
 *
 * **The same function the calendar uses**, deliberately: Home and the day grid
 * must agree on which day "today" is. Both used to read the browser's clock,
 * which opened tomorrow for anybody checking from further east after dark.
 */
export function today(timeZone: string): string {
  return todayIn(timeZone);
}

/** A booking that still has to happen — has a patient, and is not settled. */
export function isRemaining(appointment: Appointment): boolean {
  return appointment.patient !== null && !SETTLED.has(appointment.status);
}

/** Every real patient booking today, buffer blocks excluded. */
export function patientBookings(appointments: readonly Appointment[]): readonly Appointment[] {
  return appointments.filter((appointment) => appointment.patient !== null);
}

/**
 * The four headline numbers.
 *
 * **All four are computed from bookings that exist**, which is why they are not
 * the mockup's four. Its "Open slot" needs free-slot detection across the day's
 * hours and its "Payments to collect" needs a today-scoped balance query;
 * neither is built, and inventing a plausible number for a clinic to act on is
 * worse than not showing it. The screen says which two are missing rather than
 * quietly substituting.
 */
export function figuresFor(appointments: readonly Appointment[]): {
  booked: number;
  remaining: number;
  unconfirmed: number;
  inChair: number;
  seen: number;
} {
  const bookings = patientBookings(appointments);
  const count = (status: string): number =>
    bookings.filter((appointment) => appointment.status === status).length;

  return {
    booked: bookings.length,
    remaining: bookings.filter(isRemaining).length,
    unconfirmed: count("NOT_CHECKED_IN"),
    inChair: count("IN_CHAIR"),
    seen: count("COMPLETED"),
  };
}

/**
 * The next patient through the door.
 *
 * **Whoever is in the chair wins**, because "up next" during a treatment means
 * the person being treated rather than the one after them — a clinician reading
 * this mid-procedure wants the file in front of them. Otherwise it is the
 * earliest booking still to happen, which on a day that is running late is a
 * time already past; that is correct and is why it is not filtered to the
 * future.
 */
export function upNext(appointments: readonly Appointment[]): Appointment | null {
  const remaining = [...appointments]
    .filter(isRemaining)
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt));

  return remaining.find((a) => a.status === "IN_CHAIR") ?? remaining[0] ?? null;
}

export interface Watch {
  appointment: Appointment;
  kind: string;
  label: string;
  detail: string | null;
}

/**
 * What to watch for today, derived from the patients actually booked.
 *
 * **Only their critical flags.** The mockup also draws lab cases and no-show
 * risk; both need modules that do not exist — there is no lab table at all, and
 * "missed two of his last four" is a query over attendance history nothing
 * computes. Allergy and medical alerts are real today because `patient_flags`
 * carries them and `GET /appointments` already returns them with the booking,
 * so this panel costs no extra request.
 *
 * Settled bookings are excluded: an allergy matters before treatment, not after
 * the patient has gone home.
 */
export function watchToday(
  appointments: readonly Appointment[],
  risks: readonly NoShowRisk[] = [],
): readonly Watch[] {
  const watches: Watch[] = [];
  const riskByPatient = new Map(risks.map((risk) => [risk.patientId, risk]));

  for (const appointment of appointments) {
    if (!isRemaining(appointment)) continue;

    for (const flag of appointment.patient?.flags ?? []) {
      if (flag.severity !== "CRITICAL") continue;
      watches.push({
        appointment,
        kind: flagKind(flag.kind),
        label: flag.label,
        detail: flag.detail,
      });
    }

    // **Second signal, and it clears on its own** — the acceptance check asks
    // that each alert type triggers from its data and clears when the condition
    // resolves. This one does both by construction: it is recomputed from the
    // last four appointments, so attending twice removes it without anybody
    // dismissing anything.
    const risk = riskByPatient.get(appointment.patient?.id ?? "");
    if (risk !== undefined) {
      watches.push({
        appointment,
        kind: "No-show risk",
        label: `Missed ${String(risk.missed)} of their last ${String(risk.outOf)}`,
        detail: "Worth confirming before the slot is held.",
      });
    }
  }

  return watches.sort((a, b) => a.appointment.startsAt.localeCompare(b.appointment.startsAt));
}

/** `ALLERGY` → `Allergy`. The mockup's chips read this way. */
function flagKind(kind: string): string {
  const words = kind.toLowerCase().replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** `HH:MM` in the branch's own zone — the frame the clinic reads its day in. */
export function clockOf(instant: string, timezone: string): string {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: timezone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(instant));
}

/**
 * "in 12 min", "now", "40 min ago" — how far off a booking is.
 *
 * Rounded to the minute and given in whole hours past 90 minutes, because a
 * dashboard glanced at between patients does not need "in 143 minutes".
 */
export function relativeTo(instant: string, now: Date = new Date()): string {
  const minutes = Math.round((new Date(instant).getTime() - now.getTime()) / 60_000);

  if (minutes === 0) return "now";
  if (Math.abs(minutes) <= 90) {
    return minutes > 0 ? `in ${minutes} min` : `${Math.abs(minutes)} min ago`;
  }

  const hours = Math.round(Math.abs(minutes) / 60);
  return minutes > 0 ? `in ${hours} hr` : `${hours} hr ago`;
}

/**
 * `Sunday, 9 August 2026` — the mockup's own date line.
 *
 * Lives here rather than in `home-page.tsx` because the *Up next* brief needs
 * it too (STORY-087), and a second copy would eventually format two dates on
 * one screen differently.
 */
export function longDate(day: string): string {
  return new Intl.DateTimeFormat("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${day}T00:00:00Z`));
}
